import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  RoleEditContainerStaffRoleDocument,
  RoleEditContainerStaffRoleFieldsFragment,
  RoleEditContainerStaffRoleUpdateDocument,
  RoleEditContainerStaffUserCurrentDocument,
  StaffRole
} from '../../../../generated.tsx';
import { useOid } from '../../../../hooks/useOid.ts';
import { ComponentQueryLoader } from '../../../shared/component-query-loader.tsx';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../shared/utils/queryOptions';
import { RoleEdit } from './role-edit.tsx';

interface RoleEditContainerProps {}

export const RoleEditContainer: React.FC<RoleEditContainerProps> = () => {
  const navigate = useNavigate();
  const oid = useOid();
const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const { roleId } = useParams();
  const { data } = useQuery(RoleEditContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const {
    data: roleData,
    error: roleDataError,
    loading: roleDataLoading
  } = useQuery(RoleEditContainerStaffRoleDocument, {
    variables: {
      staffRoleId: roleId
    }
  });

  const [updateStaffRole, { loading: updateStaffRoleLoading }] = useMutation(RoleEditContainerStaffRoleUpdateDocument);

  const onSubmit = async (payload: RoleEditContainerStaffRoleFieldsFragment) => {
    if (data?.staffUserCurrent?.role?.roleSubtype !== 'Staff.IS') {
      payload.roleSubtype = data?.staffUserCurrent?.role?.roleSubtype ?? 'Staff.CaseWorker';
    }
    try {
      const response = await updateStaffRole({
        variables: {
          input: { ...payload, id: roleId }
        }
      });

      if (response?.data?.staffRoleUpdate?.status?.success === true) {
        navigate(`/staff/roles/detail/${roleId}`);
        message.success('Role updated successfully');
      } else {
        message.error(`Failed to update the role: ${response?.data?.staffRoleUpdate?.status?.errorMessage}`);
      }
    } catch (err: any) {
      message.error(`Failed to update the role: ${err?.message}`);
    }
  };

  return (
    <ComponentQueryLoader
      loading={roleDataLoading}
      hasData={roleData?.staffRole}
      error={roleDataError}
      hasDataComponent={
        <RoleEdit
          onSubmit={onSubmit}
          roleSubtype={data?.staffUserCurrent?.role?.roleSubtype ?? 'Staff.CaseWorker'}
          data={roleData?.staffRole as StaffRole}
          updateStaffRoleLoading={updateStaffRoleLoading}
        />
      }
    />
  );
};
